import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AdminConfig, AdminIdentity, AuthVendor } from "@spoonity/flags-contract";
import {
  getConfig,
  getMe,
  login,
  logout,
  selectVendor as selectVendorApi,
  switchVendor as switchVendorApi,
} from "../api/endpoints";
import { ApiError } from "../api/errors";
import { getToken, onTokenChange, setToken, setUnauthorizedHandler } from "./token";

/** A multi-vendor sign-in waiting for the person to pick one (CONTRACT §2.4). */
export interface PendingSelection {
  selectionToken: string;
  identities: AuthVendor[];
}

interface AuthState {
  loading: boolean;
  config: AdminConfig | null;
  identity: AdminIdentity | null;
  error: string | null;
  /** `ADMIN_AUTH_MODE=dev`: also offer the `fake:<email>` dev-token form. */
  devMode: boolean;
  /** Set between a multi-vendor `login` and `select-vendor`. */
  selection: PendingSelection | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  chooseVendor: (vendorId: string) => Promise<void>;
  cancelSelection: () => void;
  /** Identity sessions only: re-issue the session for another allowed vendor. */
  switchVendor: (vendorId: string) => Promise<void>;
  signInWithDevToken: (email: string) => Promise<void>;
  signOut: () => void;
  refreshIdentity: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.problem.title ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<PendingSelection | null>(null);

  const loadIdentity = useCallback(async () => {
    if (!getToken()) {
      setIdentity(null);
      return;
    }
    try {
      const me = await getMe();
      setIdentity(me);
      setError(null);
    } catch (err) {
      setIdentity(null);
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setToken(null);
        setError(err.status === 403 ? (err.problem.detail ?? "account not allowed") : null);
      } else {
        setError(err instanceof Error ? err.message : "failed to load identity");
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getConfig();
        if (cancelled) return;
        setConfig(cfg);
        await loadIdentity();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "failed to load config");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadIdentity]);

  useEffect(() => onTokenChange((t) => {
    if (!t) setIdentity(null);
  }), []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setIdentity(null);
      setError("Your session expired. Please sign in again.");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const adopt = useCallback(
    async (token: string) => {
      setSelection(null);
      setToken(token);
      await loadIdentity();
    },
    [loadIdentity],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const res = await login(email, password);
        if (res.requires_vendor_selection) {
          setSelection({ selectionToken: res.selection_token, identities: res.identities });
          return;
        }
        await adopt(res.token);
      } catch (err) {
        setError(messageOf(err, "Sign-in failed"));
      }
    },
    [adopt],
  );

  const chooseVendor = useCallback(
    async (vendorId: string) => {
      if (!selection) return;
      setError(null);
      try {
        const res = await selectVendorApi(selection.selectionToken, vendorId);
        await adopt(res.token);
      } catch (err) {
        // An expired selection means starting over with the password.
        if (err instanceof ApiError && err.status === 401) setSelection(null);
        setError(messageOf(err, "Could not open that vendor"));
      }
    },
    [adopt, selection],
  );

  const cancelSelection = useCallback(() => {
    setSelection(null);
    setError(null);
  }, []);

  const switchVendor = useCallback(
    async (vendorId: string) => {
      try {
        const res = await switchVendorApi(vendorId);
        setToken(res.token);
        await loadIdentity();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setToken(null);
          setIdentity(null);
          setError("Your session expired. Please sign in again.");
          return;
        }
        setError(messageOf(err, "Could not switch vendor"));
      }
    },
    [loadIdentity],
  );

  const signInWithDevToken = useCallback(
    async (email: string) => {
      setToken(`fake:${email}`);
      await loadIdentity();
    },
    [loadIdentity],
  );

  const signOut = useCallback(() => {
    void logout().catch(() => undefined);
    setToken(null);
    setIdentity(null);
    setSelection(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      config,
      identity,
      error,
      devMode: config?.authMode === "dev",
      selection,
      signInWithPassword,
      chooseVendor,
      cancelSelection,
      switchVendor,
      signInWithDevToken,
      signOut,
      refreshIdentity: loadIdentity,
    }),
    [
      loading,
      config,
      identity,
      error,
      selection,
      signInWithPassword,
      chooseVendor,
      cancelSelection,
      switchVendor,
      signInWithDevToken,
      signOut,
      loadIdentity,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function canWrite(role: AdminIdentity["role"] | undefined): boolean {
  return role === "editor" || role === "admin";
}

export function isAdmin(role: AdminIdentity["role"] | undefined): boolean {
  return role === "admin";
}
